import React from 'react';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpwardRounded';
import ArrowDownward from '@material-ui/icons/ArrowDownwardRounded';
import Box from '@material-ui/core/Box';
import moment from 'moment';
import numeral from 'numeral';

const ArrowUpIcon = (
  <Box
    border="2px solid #fff"
    borderRadius="50%"
    display="flex"
    alignItems="center"
    justifyContent="center"
    height="25px"
    width="25px"
    color="#fff"
    ml="auto"
  >
    <ArrowUpwardIcon fontSize="small" style={{ fontSize: '1.1rem' }} />
  </Box>
);

const ArrowDownIcon = (
  <Box
    border="2px solid #fff"
    borderRadius="50%"
    display="flex"
    alignItems="center"
    justifyContent="center"
    height="25px"
    width="25px"
    color="#fff"
    ml="auto"
  >
    <ArrowDownward fontSize="small" style={{ fontSize: '1.1rem' }} />
  </Box>
);

export const timeSeriesNameToColor = name => {
  if (name === 'Indexes') {
    return {
      backgroundColorBasedOnSetName: '#757575',
      textColorBasedOnSetName: '#fff'
    };
  }

  if (name === 'Portfolios Actual') {
    return {
      backgroundColorBasedOnSetName: '#1565c0',
      textColorBasedOnSetName: '#fff'
    };
  }

  if (name === 'Proposed Managers') {
    return { backgroundColorBasedOnSetName: '#fbc02d', textColorBasedOnSetName: '#000' };
  }
  return {};
};

export const performanceToColor = performance => {
  if (performance > 0.02) {
    return { backgroundColor: '#009B0C', icon: ArrowUpIcon, textColor: '#fff' };
  }
  if (performance && performance <= 0) {
    return { backgroundColor: '#A32B2B', icon: ArrowDownIcon, textColor: '#fff' };
  }
  if (performance > 0 && performance <= 0.02) {
    return { backgroundColor: '#AAE946', icon: ArrowUpIcon, textColor: '#000' };
  }
  return { backgroundColor: '#2B2B2B', icon: ArrowDownIcon, textColor: '#fff' };
};

export const handleCellColor = value => {
  if (value > 0.02) {
    return '#009B0C';
  }
  if (value && value <= 0) {
    return '#A32B2B';
  }
  if (value > 0 && value <= 0.02) {
    return '#AAE946';
  }
  return '#346C9F';
};

export const performanceToIcon = performance => {
  if (performance > 1) {
    return '#009B0C';
  }
  if (performance <= 0) {
    return '#A32B2B';
  }
  return '#AAE946';
};

export const calculateDynamicTimeIntervals = id => {
  switch (id) {
    case 1:
      // TODO: FIXME:
      return {
        startPeriod: 'Since Inception',
        endPeriod: moment().format('MM.DD.YYYY')
      };
    case 2:
      return {
        startPeriod: moment().startOf('year').format('MM.DD.YYYY'),
        endPeriod: moment().format('MM.DD.YYYY')
      };
    case 3:
      return {
        startPeriod: moment().subtract(1, 'year').format('MM.DD.YYYY'),
        endPeriod: moment().format('MM.DD.YYYY')
      };
    case 4:
      return {
        startPeriod: moment().subtract(1, 'year').subtract(1, 'month').format('MM.DD.YYYY'),
        endPeriod: moment().format('MM.DD.YYYY')
      };
    case 5:
      return {
        startPeriod: moment().subtract(2, 'year').format('MM.DD.YYYY'),
        endPeriod: moment().format('MM.DD.YYYY')
      };
    case 6:
      return {
        startPeriod: moment().subtract(3, 'year').format('MM.DD.YYYY'),
        endPeriod: moment().format('MM.DD.YYYY')
      };

    default:
      return null;
  }
};

export const parseObjectValuesToBool = object => {
  const parsedObject = {};

  // eslint-disable-next-line no-restricted-syntax
  for (const [key, value] of Object.entries(object)) {
    parsedObject[key] = !!value;
  }

  return parsedObject;
};

export const calculateDisplayValueFormatting = (type, value) => {
  if (type === 'value') {
    const formattedValue = numeral(value).format('0.00%');
    return formattedValue;
  }
  return value;
};

export const calculateHomeBarChartAxis = type => {
  if (type === 'value') {
    return 'Performance';
  }
  return 'Invested';
};

export function sortHeatmapArray(type, data) {
  if (type !== 'name') {
    const sorted = [...data].sort((a, b) => b[type] - a[type]);
    const nullValues = sorted.filter((item) => item.value === null || item.value === 0);
    const nonNullValues = sorted.filter((item) => item.value !== null && item.value !== 0);
    return nonNullValues.concat(nullValues);
  }
  const sortedAlphabetically = [...data].sort((a, b) => {
    if (a[type] < b[type]) {
      return -1;
    }
    if (a[type] > b[type]) {
      return 1;
    }
    return 0;
  });
  const nullValues = sortedAlphabetically.filter((item) => item.value === null || item.value === 0);
  const nonNullValues = sortedAlphabetically.filter((item) => item.value !== null && item.value !== 0);
  return nonNullValues.concat(nullValues);
}

export const formatPnlToMatchInvestment = (periodPnl, investment) => {
  if (!periodPnl) {
    return;
  }

  if (!investment) {
    const pnlValue = numeral(periodPnl).format('0.00 a');
    // eslint-disable-next-line consistent-return
    return pnlValue;
  }

  const formattedInvestment = numeral(investment).format('0.00 a');

  const dividerValue = periodPnl / investment;

  const indexOfTest = formattedInvestment.indexOf('M');

  const formattedInvestmentNumber = formattedInvestment.slice(0, indexOfTest);
  const formattedPnlSuffix = formattedInvestment.slice(indexOfTest);

  const formattedPnlNumber = (Number(formattedInvestmentNumber) * dividerValue).toFixed(1);

  const pnlDisplayValue = `${formattedPnlNumber} ${formattedPnlSuffix}`;

  // eslint-disable-next-line consistent-return
  return pnlDisplayValue;
};

export const formatRoRToMatchInvestment = (value, investment, external) => {
  if (!value) {
    return;
  }
  const formattedInvestment = numeral(investment).format('0.00 a');
  const formattedPerformance = Number(value * 100).toFixed(2);
  const indexOfTest = formattedInvestment.indexOf('M');
  const formattedInvestmentSuffixLength = formattedInvestment.slice(indexOfTest).length;
  const isSingleSymbolSuffix = formattedInvestmentSuffixLength < 2;

  const performanceDisplayValue = (
    <>
      <span
        style={{
          paddingRight: !external ? (isSingleSymbolSuffix ? '7px' : '17px') : '0px',
          display: 'inline-block',
          fontSize: external ? '16px' : '12px',
        }}
      >
        {formattedPerformance}
      </span>
      {external ? <span style={{ fontSize: '16px' }}>%</span> :
      <span style={{ display: 'inline-block', float: 'left' }}>%</span>}

      <span style={{ width: '22px',
       display: 'inline-block',
       fontSize: external ? '16px' : '12px',
       paddingLeft: external && '5px' }}>RoR</span>
    </>
  );

  // eslint-disable-next-line consistent-return
  return performanceDisplayValue;
};


export const removeLongShortSuffixFromChartName = name => {
  const indexOfUnderscore = name?.indexOf('_');

  const hasNameWithUnderscore = indexOfUnderscore > -1;

  if (!hasNameWithUnderscore) return name;

  const formattedChartName = name.replace('_', ' ');

  return formattedChartName;
};

export const calculateHeatmapDefaultDate = () => {
  const startDate = moment().startOf('month');
  const todayDate = moment(new Date());

  const difference = todayDate.diff(startDate, 'days');
  if (difference >= 10) {
    return moment().format('MMM YYYY');
  }

  const lastMonth = moment().subtract(1, 'month').format('MMM YYYY');
  return lastMonth;
};

export const mapWidgetType = type => {
  switch (type.toLowerCase()) {
    case 'chart':
      return 1;
    case 'table':
      return 2;
    case 'custom':
      return 3;
    default:
      return 0;
  }
};

export const inheritDashboardProperties = (widgetData, dashboardData) => {
  return {
    ...widgetData,
    dateRangeTypeId: widgetData.dateRangeTypeId || dashboardData.dateRangeTypeId,
    masterObjectId: widgetData.masterObjectId || dashboardData.masterObjectId,
    masterObjectName: widgetData.masterObjectName || dashboardData.masterObjectName,
    objectTypeDetailId: widgetData.objectTypeDetailId || dashboardData.objectTypeDetailId,
    masterObjectType: widgetData.masterObjectType || dashboardData.masterObjectType,
    benchmarks: widgetData.benchmarks.length > 0 ? widgetData.benchmarks : dashboardData.benchmarks
  };
};

export const formatDashboardContextMenuRequestParams = ({ props, data }) => {
  const params = {
    ...props,
    operation: data
  };

  switch (data) {
    case 'insert_before': {
      return {
        firstOrdinal: props.tabOrdinal,
        secondOrdinal: null,
        ...params
      };
    }
    case 'insert_after': {
      return {
        firstOrdinal: props.tabOrdinal + 1,
        secondOrdinal: null,
        ...params
      };
    }

    case 'replace': {
      return {
        firstOrdinal: props.tabOrdinal,
        secondOrdinal: props.tabOrdinal,
        ...params
      };
    }
    case 'delete': {
      return {
        firstOrdinal: props.tabOrdinal,
        secondOrdinal: props.tabOrdinal,
        ...params
      };
    }

    case 'swap': {
      return {
        secondOrdinal: props.tabOrdinal,
        ...params
      };
    }
    case 'reset': {
      return {
        ...params
      };
    }
    case 'save': {
      return {
        ...params
      };
    }
    default: {
      return {
        firstOrdinal: null,
        secondOrdinal: null,
        ...params
      };
    }
  }
};

export const extractTabProps = id => {
  const splitTabIdAndOrdinalPosition = id.split('@');
  const tabId = splitTabIdAndOrdinalPosition[0];
  const tabOrdinal = Number(splitTabIdAndOrdinalPosition[1]);

  return { tabId, tabOrdinal };
};

// Data Source helper functions

export const formatLabelDate = date => {
  const isQuartile = date.startsWith('Q');
  if (isQuartile) return date;

  const formattedDate = moment(date).format('MMM-YY');

  if (formattedDate === 'Invalid date') {
    const alreadyFormattedDate = moment(date, 'MM/YYYY').format('MMM-YY');

    if (alreadyFormattedDate === 'Invalid date') return date;

    return alreadyFormattedDate;
  }
  return formattedDate;
};

const calculateNumberOfDisplayedLabels = colSpan => {
  switch (colSpan) {
    case 12:
    case 10:
      return 15;
    case 9:
    case 8:
      return 12;
    case 7:
    case 6:
      return 9;
    case 5:
      return 7;
    case 4:
      return 6;
    case 3:
      return 5;
    case 2:
      return 4;
    default:
      return null;
  }
};

export const calculateLabelStep = (categoriesLength, colSpan) => {
  const numberOfLabelsPerAxes = calculateNumberOfDisplayedLabels(colSpan);
  const labelStep = Math.ceil(categoriesLength / numberOfLabelsPerAxes);
  return labelStep;
};

export const calculatePdfImageSizeInMM = (offsetHeight, offsetWidth) => {
  const heightInMM = (offsetHeight * 297) / offsetWidth;
  if (heightInMM <= 210) {
    return { h: heightInMM, w: 297 };
  }
  return { h: heightInMM * 0.5, w: 148.5 };
};

export const calculatePdfImageCoordinatesInMM = (offsetHeight, offsetWidth) => {
  const { h, w } = calculatePdfImageSizeInMM(offsetHeight, offsetWidth);
  const y = (210 - h) / 2;
  const x = (297 - w) / 2;

  if (y > 0 && x > 0) {
    return { x, y };
  }
  if (y > 0) {
    return { x: 0, y };
  }
  return { x, y: 0 };
};

export const calculateStringFromBool = bool => {
  return bool ? 'Yes' : 'No';
};

export const updateNavigationDashboards = ({
  portfolios,
  holdings,
  masterObjectId,
  dashboards
}) => {
  const isNavigationPortfolio = portfolios.findIndex(portfolio => portfolio.id === masterObjectId);
  const isNavigationHolding = holdings.findIndex(holding => holding.id === masterObjectId);

  if (isNavigationPortfolio >= 0) {
    const updatedPortfolioDashboards = {
      ...portfolios[isNavigationPortfolio],
      dashboards
    };

    // Changes the value of user context property directly! Keep in mind!
    portfolios.splice(isNavigationPortfolio, 1, updatedPortfolioDashboards);
  }

  if (isNavigationHolding >= 0) {
    const updatedHoldingDashboards = {
      ...holdings[isNavigationHolding],
      dashboards
    };

    // Changes the value of user context property directly! Keep in mind!
    holdings.splice(isNavigationHolding, 1, updatedHoldingDashboards);
  }
};

export const updateNavigationDashboardsItem = ({
  portfolios,
  holdings,
  masterObjectId,
  id,
  dashboardId
}) => {
  const isNavigationPortfolio = portfolios.findIndex(portfolio => portfolio.id === masterObjectId);
  const isNavigationHolding = holdings.findIndex(holding => holding.id === masterObjectId);

  if (isNavigationPortfolio >= 0) {
    const navDashIndex = portfolios[isNavigationPortfolio].dashboards.findIndex(
      item => item.id === id
    );
    const updatedDashTab = {
      ...portfolios[isNavigationPortfolio].dashboards[navDashIndex],
      id: dashboardId
    };

    // Changes the value of user context property directly! Keep in mind!
    portfolios[isNavigationPortfolio].dashboards.splice(navDashIndex, 1, updatedDashTab);
  }

  if (isNavigationHolding >= 0) {
    const navDashIndex = holdings[isNavigationHolding].dashboards.findIndex(item => item.id === id);
    const updatedDashTab = {
      ...holdings[isNavigationHolding].dashboards[navDashIndex],
      id: dashboardId
    };

    // Changes the value of user context property directly! Keep in mind!
    holdings[isNavigationHolding].dashboards.splice(navDashIndex, 1, updatedDashTab);
  }
};

export const isChartWidgetType = type => type === 'Chart';

export const calculateWidthFromId = id => {
  switch (id) {
    case 6:
      return 2;
    case 7:
      return 3;
    case 8:
      return 4;
    case 9:
      return 6;
    case 10:
      return 12;

    default:
      return 12;
  }
};

export const calculateLayoutSlotsFromId = id => {
  switch (id) {
    case 1:
      return 11;
    case 2:
      return 10;
    case 3:
      return 12;
    case 4:
      return 13;
    case 5:
      return 10;
    case 6:
      return 30;
    case 7:
      return 20;
    case 8:
      return 15;
    case 9:
      return 10;
    case 10:
      return 5;

    default:
      return null;
  }
};

export const customStackedColumnChartBAnchorColors = index => {
  let color = '#1595eb';
  const radius = 4;
  const thickness = 4;
  let sides = 0;

  switch (index) {
    case 0: // purple circle
      color = '#9b34eb';
      sides = 0;
      break;
    case 1: // blue circle
      color = '#1543eb';
      sides = 0;
      break;
    case 2: // pink hexagon
      color = '#d915eb';
      sides = 6;
      break;
    case 3: // red triangle
      color = '#f54242';
      sides = 3;
      break;
    default:
      // random shape with random color
      color = `#${Math.floor(Math.random() * 16777215).toString(16)}`;
      sides = Math.floor(Math.random() * 6) + 1;
      break;
  }
  const anchorProps = {
    anchorRadius: radius,
    anchorBorderThickness: thickness,
    anchorBorderColor: color,
    anchorSides: sides,
    anchorBgColor: color
  };
  return anchorProps;
};

export const buildDataSet = minMaxValues => {
  const arr = [];
  const dataset = [
    { data: [] },
    { data: [] },
    { data: [] },
    { data: [] },
    { data: [] },
    { data: [] },
    { data: [] },
    { data: [] }
  ];
  minMaxValues.map(values => {
    const firstValue = values.data[0].max - values.data[0].min;
    const data = [];
    data.push({
      value: firstValue,
      alpha: '0',
      toolText: `Values start from ${firstValue.toFixed(2)}`
    });
    values.data.map(val => {
      data.push({ value: val.max - val.min });
    });
    arr.push(data);
  });

  arr.map((values, i) => {
    values.map((v, p) => {
      dataset[p].data[i] = v;
    });
  });
  return dataset;
};

export const multiplierCalculator = suffix => {
  switch (suffix) {
    case '%':
      return 100;
    default:
      return 1;
  }
};
